import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';

/** Public surface of the ClickMassa widget that this adapter relies on. */
interface SupportChatSdk {
  toggle(): void;
  close(): void;
}

/**
 * `supportChatReady` is set by the loader in `apps/platform-shell-app/src/index.html`
 * when the widget reports ready. The SDK's `on('ready')` never replays an event that
 * already fired, so the flag is the only way to learn about a `ready` that happened
 * before this store existed.
 */
type SupportChatWindow = Window &
  typeof globalThis & {
  $envioMensagemChat?: SupportChatSdk;
  supportChatReady?: boolean;
};

interface SupportChatState {
  /** The vendor widget reported ready. Stays false when the SDK never loads. */
  available: boolean;
  /** The vendor panel is open. */
  open: boolean;
  /** Verbatim text of the vendor unread badge (`'1'`..`'9'`, `'9+'`); `''` when hidden. */
  unreadLabel: string;
}

const initialState: SupportChatState = {
  available: false,
  open: false,
  unreadLabel: '',
};

const READY_EVENT = 'envioMensagem:ready';
const OPENED_EVENT = 'envioMensagem:opened';
const CLOSED_EVENT = 'envioMensagem:closed';
const FRAME_SELECTOR = '.vtx-sdk-frame';
const BADGE_SELECTOR = '.vtx-sdk-badge';
const OPEN_CLASS = 'vtx-open';

/**
 * Adapter over the third-party support chat widget (ClickMassa). The widget draws
 * itself on `<body>` and is optional: blockers leave its globals undefined, in which
 * case `available` stays false and every method is a no-op.
 */
export const SupportChatStore = signalStore(
  { providedIn: 'root' },
  withState<SupportChatState>(initialState),
  withMethods((store, document = inject(DOCUMENT)) => {
    const sdk = (): SupportChatSdk | undefined =>
      (document.defaultView as SupportChatWindow | null)?.$envioMensagemChat;

    return {
      toggle(): void {
        sdk()?.toggle();
      },
      close(): void {
        if (!store.open()) {
          return;
        }
        sdk()?.close();
      },
    };
  }),
  withHooks((store, document = inject(DOCUMENT)) => {
    const view = document.defaultView as SupportChatWindow | null;
    let badgeObserver: MutationObserver | null = null;

    // The unread count is not part of the vendor's public contract: the SDK only
    // writes it to its own badge (`textContent` + inline `display`). Mirror that
    // element and degrade to "no counter" whenever it is not what we expect.
    const readBadge = (badge: HTMLElement): string =>
      badge.style.display === 'none' ? '' : (badge.textContent ?? '').trim();

    const watchBadge = (): void => {
      badgeObserver?.disconnect();
      badgeObserver = null;

      const badge = document.querySelector<HTMLElement>(BADGE_SELECTOR);
      const Observer = view?.MutationObserver;
      if (!badge || !Observer) {
        patchState(store, { unreadLabel: '' });
        return;
      }

      patchState(store, { unreadLabel: readBadge(badge) });
      badgeObserver = new Observer(() => patchState(store, { unreadLabel: readBadge(badge) }));
      badgeObserver.observe(badge, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });
    };

    const isFrameOpen = (): boolean =>
      document.querySelector(FRAME_SELECTOR)?.classList.contains(OPEN_CLASS) ?? false;

    // Idempotent: the SDK runs `on('ready')` callbacks and then dispatches the window
    // event, and the iframe may report ready again after a reload.
    const onReady = (): void => {
      patchState(store, { available: true, open: isFrameOpen() });
      watchBadge();
    };
    const onOpened = (): void => patchState(store, { open: true });
    const onClosed = (): void => patchState(store, { open: false });

    return {
      onInit(): void {
        if (!view) {
          return;
        }
        view.addEventListener(READY_EVENT, onReady);
        view.addEventListener(OPENED_EVENT, onOpened);
        view.addEventListener(CLOSED_EVENT, onClosed);

        patchState(store, { open: isFrameOpen() });
        if (view.supportChatReady === true) {
          onReady();
        }
      },
      onDestroy(): void {
        view?.removeEventListener(READY_EVENT, onReady);
        view?.removeEventListener(OPENED_EVENT, onOpened);
        view?.removeEventListener(CLOSED_EVENT, onClosed);
        badgeObserver?.disconnect();
        badgeObserver = null;
      },
    };
  }),
);
